/**
 * useNavParams — 持久化 ?nav=false 等查询参数到所有内部链接
 *
 * 在 standalone / embedded 模式下，?nav=false 用于隐藏侧边栏/顶栏。
 * 此 hook 确保页面内所有 Link / NavLink 跳转都自动携带该参数。
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

/** Query params that should persist across navigation */
const PERSISTENT_PARAMS = ['nav'] as const;

/**
 * Returns a function that appends persistent query params to a path.
 *
 * Usage:
 *   const linkTo = usePersistentLink();
 *   <Link to={linkTo('/e/jobs')}>Jobs</Link>
 */
export function usePersistentLink() {
  const [searchParams] = useSearchParams();

  return useCallback(
    (to: string): string => {
      const persistedEntries: [string, string][] = [];
      for (const key of PERSISTENT_PARAMS) {
        const value = searchParams.get(key);
        if (value !== null) {
          persistedEntries.push([key, value]);
        }
      }

      if (persistedEntries.length === 0) return to;

      // Parse existing query in `to`
      const [path, existingQuery] = to.split('?');
      const params = new URLSearchParams(existingQuery ?? '');
      for (const [k, v] of persistedEntries) {
        if (!params.has(k)) params.set(k, v);
      }
      return `${path}?${params.toString()}`;
    },
    [searchParams],
  );
}
